// App.js
import React, { useEffect, useState, useCallback } from "react";
import { io } from "socket.io-client";
import GameBoard from "./components/GameBoard";
import { ethers } from "ethers";
// MetaMask flow: import your contract addresses and ABIs
import { OPOL_CONTRACT_ADDRESS, OPOL_ABI } from "./contractConfig";
import { nftContractAddress, nftContractABI } from "./nftContractConfig";

// ==================== Thirdweb Imports (for Thirdweb connection) ====================
import {
  ThirdwebProvider,
  ConnectButton,
  useActiveAccount,
  useSendTransaction,
  useWaitForReceipt,
  darkTheme,
} from "thirdweb/react";
import {
  inAppWallet,
  createWallet,
} from "thirdweb/wallets";
import { createThirdwebClient, getContract, prepareContractCall } from "thirdweb";

// Import the ERC20 balanceOf extension function
import { balanceOf as erc20BalanceOf } from "thirdweb/extensions/erc20";
// Import the production polygon chain object
import { polygon } from "thirdweb/chains";

// Create the Thirdweb client
const client = createThirdwebClient({
  clientId:
    process.env.NEXT_PUBLIC_THIRDWEB_CLIENT_ID ||
    "6ad44c22103b8eceb119d19ecff8181b",
});

const wallets = [
  inAppWallet({
    auth: {
      options: [
        "google",
        "discord",
        "telegram",
        "farcaster",
        "email",
        "x",
        "passkey",
        "phone",
        "apple",
        "facebook",
        "line",
        "coinbase",
        "steam",
        "twitch",
      ],
    },
  }),
  createWallet("io.metamask"),
  createWallet("com.coinbase.wallet"),
  createWallet("me.rainbow"),
  createWallet("io.rabby"),
  createWallet("io.zerion.wallet"),
  createWallet("com.trustwallet.app"),
  createWallet("com.binance"),
  createWallet("org.uniswap"),
  createWallet("com.safepal"),
  createWallet("pro.tokenpocket"),
  createWallet("com.ledger"),
  createWallet("global.safe"),
  createWallet("xyz.timelesswallet"),
  createWallet("com.robinhood.wallet"),
  createWallet("io.1inch.wallet"),
  createWallet("com.fireblocks"),
  createWallet("app.phantom"),
  createWallet("com.roninchain.wallet"),
  createWallet("org.mathwallet"),
  createWallet("com.alphawallet"),
  createWallet("com.unstoppabledomains"),
  createWallet("xyz.frontier.wallet"),
  createWallet("app.keyring"),
  createWallet("technology.obvious"),
  createWallet("app.omni"),
  createWallet("com.ambire"),
  createWallet("com.mtpelerin"),
  createWallet("app.walletnow"),
  createWallet("com.bitcoin"),
  createWallet("io.alpha-u.wallet.web"),
  createWallet("io.myabcwallet"),
  createWallet("com.opera"),
  createWallet("co.arculus"),
  createWallet("com.coin98"),
  createWallet("com.chain"),
  createWallet("io.huddln"),
  createWallet("me.haha"),
  createWallet("com.get-verso"),
  createWallet("pk.modular"),
  createWallet("org.kelp"),
  createWallet("com.kraken"),
  createWallet("io.magiceden.wallet"),
  createWallet("app.backpack"),
  createWallet("com.zengo"),
  createWallet("com.ripio"),
  createWallet("com.broearn"),
  createWallet("io.clingon"),
  createWallet("com.coinomi"),
  createWallet("com.sabay.wallet"),
  createWallet("io.copiosa"),
  createWallet("world.fncy"),
  createWallet("io.certhis"),
  createWallet("io.ancrypto"),
];

// ==================== Socket.IO Initialization ====================
const socket = io("https://games.onlypolyverse.com");
window.socket = socket; // For debugging

// ------------------ Helper: Parse tokenId from logs ------------------
function parseTokenIdFromLogs(logs) {
  // Standard ERC721 Transfer event signature:
  const transferEventSig = ethers.utils.id("Transfer(address,address,uint256)");
  for (const log of logs) {
    if (log.topics[0] === transferEventSig) {
      // In the Transfer event, tokenId is in the 4th topic (index 3)
      const tokenIdHex = log.topics[3];
      return ethers.BigNumber.from(tokenIdHex).toString();
    }
  }
  return null;
}

const App = () => {
  // ------------------ Common Game State ------------------
  const [walletAddress, setWalletAddress] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  // In‑game polyBalance (maintained by the server) starts at 1500.
  const [polyBalance, setPolyBalance] = useState(1500);
  const [gameState, setGameState] = useState({});
  const [playerColors, setPlayerColors] = useState({});
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [properties, setProperties] = useState([]);
  const [diceRolls, setDiceRolls] = useState({});
  const [isGameOver, setIsGameOver] = useState(false);
  const [winner, setWinner] = useState(null);
  const [gameId, setGameId] = useState(null);
  const [playerOrder, setPlayerOrder] = useState([]);
  const [swapProposal, setSwapProposal] = useState(null);
  const [showSwapProposalModal, setShowSwapProposalModal] = useState(false);
  const [mintedSets, setMintedSets] = useState({});
  const [turnTimeLeft, setTurnTimeLeft] = useState(60);
  const [isJoiningGame, setIsJoiningGame] = useState(false);

  // ------------------ MetaMask (Manual) Connection State ------------------
  const [metaWallet, setMetaWallet] = useState(null);
  const [metaOpolContract, setMetaOpolContract] = useState(null);
  const [metaOpolBalance, setMetaOpolBalance] = useState(0);

  // ------------------ Thirdweb Connection State ------------------
  const [thirdWallet, setThirdWallet] = useState(null);
  const [thirdOpolContract, setThirdOpolContract] = useState(null);
  const [thirdOpolBalance, setThirdOpolBalance] = useState(0);
  const [nftContractInstance, setNftContractInstance] = useState(null);

  // ------------------ State for Transaction Hashes ------------------
  const [topUpTxHash, setTopUpTxHash] = useState(null);
  const [nftTxHash, setNftTxHash] = useState(null);

  // ------------------ Temporary NFT Data ------------------
  const [currentVerificationHash, setCurrentVerificationHash] = useState(null);
  const [currentIpfsUri, setCurrentIpfsUri] = useState(null);

  // ------------------ Thirdweb Hooks ------------------
  const activeAccount = useActiveAccount();
  // Configure the Thirdweb sendTransaction hook with a customized pay modal.
  // This pre-fills the native token purchase on the Polygon chain and enables the user to edit the amount.
  const { mutate: sendTransaction } = useSendTransaction({
    payModal: {
      prefillBuy: {
        chain: polygon,
        allowEdits: {
          amount: true, // allows the user to modify the top-up amount
          token: false,
          chain: false,
        },
      },
      preferredProvider: "STRIPE" | "KADO" | "TRANSAK", 
      theme: "dark",
    },
  });

  // ------------------ Helper: Determine Active Values ------------------
  // Prefer MetaMask values over Thirdweb if available.
  const activeOpolContract = metaWallet ? metaOpolContract : thirdWallet ? thirdOpolContract : null;
  // The on‑chain OPOL balance is only for informational purposes;
  // the in‑game polyBalance is maintained by the server.
  const activeOpolBalance = metaWallet ? metaOpolBalance : thirdWallet ? thirdOpolBalance : 0;

  // ------------------ useWaitForReceipt Hooks ------------------
  const { data: topUpReceipt } = useWaitForReceipt(
    topUpTxHash ? { client, chain: polygon, transactionHash: topUpTxHash } : undefined
  );
  const { data: nftReceipt } = useWaitForReceipt(
    nftTxHash ? { client, chain: polygon, transactionHash: nftTxHash } : undefined
  );

  // ------------------ useEffect: Process Top-Up Receipt ------------------
  useEffect(() => {
    if (topUpReceipt) {
      alert("Smart contract top-up successful (Thirdweb).");
      socket.emit("top-up", { gameId, walletAddress, amount: 1500 });
      socket.emit("request-balance", { walletAddress, gameId });
      setTopUpTxHash(null);
    }
  }, [topUpReceipt, gameId, walletAddress]);

  // ------------------ useEffect: Process NFT Mint Receipt ------------------
  useEffect(() => {
    if (nftReceipt) {
      const tokenId = parseTokenIdFromLogs(nftReceipt.logs);
      if (!tokenId) {
        alert("Token ID not found in transaction receipt (Thirdweb).");
      } else {
        fetch("/update-verification-ipfs", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            verificationHash: currentVerificationHash,
            ipfsUri: currentIpfsUri,
            tokenId,
          }),
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.error) {
              alert(data.error);
            } else {
              alert(`NFT minted and verification updated successfully! Token ID: ${tokenId}`);
            }
          })
          .catch((err) => {
            console.error(err);
            alert("Error updating verification on server.");
          });
      }
      setNftTxHash(null);
      setCurrentVerificationHash(null);
      setCurrentIpfsUri(null);
    }
  }, [nftReceipt, currentVerificationHash, currentIpfsUri]);

  // =================== Original MetaMask Connection (Unchanged) ===================
  const connectWalletWith = async () => {
    try {
      if (window.ethereum && window.ethereum.isMetaMask) {
        console.log("Using MetaMask provider from window.ethereum.");
        const provider = window.ethereum;
        await provider.request({ method: "eth_requestAccounts" });
        const ethersProvider = new ethers.providers.Web3Provider(provider);
        const signer = ethersProvider.getSigner();
        const account = await signer.getAddress();

        setWalletAddress(account);
        setMetaWallet(account);
        setIsConnected(true);

        // Check network => Polygon Mainnet (chainId 137)
        const network = await ethersProvider.getNetwork();
        if (network.chainId !== 137) {
          try {
            await provider.request({
              method: "wallet_switchEthereumChain",
              params: [{ chainId: "0x89" }],
            });
            alert("Switched to Polygon Mainnet.");
          } catch (err) {
            console.error("Failed to switch to Polygon:", err);
            return;
          }
        }

        // Create OPOL contract instance using ethers.Contract (manual ABI)
        const contract = new ethers.Contract(
          OPOL_CONTRACT_ADDRESS,
          OPOL_ABI,
          signer
        );
        setMetaOpolContract(contract);

        // Fetch OPOL balance using the manual contract call.
        const balance = await contract.balanceOf(account);
        const formatted = ethers.utils.formatEther(balance);
        const initial = formatted === "0.0" ? 1500 : formatted;
        setMetaOpolBalance(initial);

        // Ensure the in‑game poly balance starts at 1500.
        setPolyBalance(1500);

        // Join game and request an immediate balance update from the server.
        setIsJoiningGame(true);
        await joinGameServer(account);
        socket.emit("request-balance", { walletAddress: account, gameId });
      } else {
        alert("MetaMask is not available. Please install MetaMask.");
      }
    } catch (err) {
      console.error("Failed to connect wallet (MetaMask):", err);
      alert("Failed to connect wallet. Check console for details.");
    }
  };

  // =================== Thirdweb Connection (New) ===================
  useEffect(() => {
    // Use Thirdweb connection only if activeAccount exists and MetaMask is not in use.
    if (activeAccount && activeAccount.address && !metaWallet) {
      const account = activeAccount.address;
      setWalletAddress(account);
      setThirdWallet(account);
      setIsConnected(true);
      // Always initialize in‑game polyBalance to 1500.
      setPolyBalance(1500);

      // Load the OPOL contract via Thirdweb's getContract (with ABI and chain)
      const opol = getContract({
        client,
        address: OPOL_CONTRACT_ADDRESS,
        abi: OPOL_ABI,
        chain: polygon,
      });
      setThirdOpolContract(opol);

      // Load the NFT contract via Thirdweb's getContract (with ABI and chain)
      const nft = getContract({
        client,
        address: nftContractAddress,
        abi: nftContractABI,
        chain: polygon,
      });
      setNftContractInstance(nft);

      // Fetch the on‑chain OPOL balance (for informational purposes)
      erc20BalanceOf({ contract: opol, address: account })
        .then((balance) => {
          const formatted = ethers.utils.formatEther(balance);
          setThirdOpolBalance(formatted);
          setPolyBalance(1500);
        })
        .catch((err) =>
          console.error("Error fetching OPOL balance (Thirdweb):", err)
        );

      // Automatically join game and request balance update.
      joinGameServer(account);
      socket.emit("request-balance", { walletAddress: account, gameId });
    }
  }, [activeAccount, metaWallet]);

  // =================== joinGameServer (Common) ===================
  const joinGameServer = async (account) => {
    try {
      const response = await fetch("https://games.onlypolyverse.com/start-game", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ walletAddress: account }),
      });
      const data = await response.json();
      if (data.error) {
        alert(data.error);
        setIsJoiningGame(false);
        return;
      }
      setGameId(data.gameId);
      socket.emit("player-connected", { walletAddress: account, gameId: data.gameId });
      socket.emit("request-balance", { walletAddress: account, gameId: data.gameId });
    } catch (err) {
      console.error("Error joining game on server:", err);
      alert("Could not join the game. See console.");
    } finally {
      setIsJoiningGame(false);
    }
  };

  // =================== handleTopUp ===================
  const handleTopUp = async () => {
    // --- MetaMask Flow ---
    if (metaWallet) {
      if (!metaOpolContract) {
        alert("OPOL contract not loaded (MetaMask).");
        return;
      }
      try {
        const updatedBal = await metaOpolContract.balanceOf(walletAddress);
        const userOpolBal = parseFloat(ethers.utils.formatEther(updatedBal));
        let tx;
        if (userOpolBal >= 1500) {
          tx = await metaOpolContract.joinGame({ value: ethers.utils.parseEther("0") });
        } else {
          tx = await metaOpolContract.joinGame({ value: ethers.utils.parseEther("3") });
        }
        await tx.wait();
        alert("Smart contract top-up successful (MetaMask).");
        socket.emit("top-up", { gameId, walletAddress, amount: 1500 });
        setPolyBalance((prev) => prev + 1500);
      } catch (err) {
        console.error("Top Up error (MetaMask):", err);
        alert("Top-up failed. See console for details.");
      }
    }
    // --- Thirdweb Flow ---
    else if (thirdWallet) {
      if (!thirdOpolContract) {
        alert("OPOL contract not loaded (Thirdweb).");
        return;
      }
      try {
        const updatedBal = await erc20BalanceOf({ contract: thirdOpolContract, address: walletAddress });
        const userOpolBal = parseFloat(ethers.utils.formatEther(updatedBal));
        // If the user is below 1500 OPOL, prefill the modal with "3.3" POL (the user can edit this amount in the Thirdweb Pay modal)
        const sendValue = userOpolBal >= 1500 ? "0" : "3.3";
        const transaction = prepareContractCall({
          contract: thirdOpolContract,
          method: "joinGame",
          params: [],
          overrides: { value: ethers.utils.parseEther(sendValue) },
        });
        // Use sendTransaction with callbacks. The customised Pay modal will allow the user to edit the amount.
        sendTransaction(transaction, {
          onSuccess: (txResult) => {
            setTopUpTxHash(txResult.transactionHash);
          },
          onError: (error) => {
            console.error("Top Up error (Thirdweb):", error);
            alert("Top-up failed. See console for details.");
          },
        });
      } catch (err) {
        console.error("Error in handleTopUp (Thirdweb):", err);
        alert("Top-up failed. See console for details.");
      }
    }
  };

  // =================== mintNFTAndVerify ===================
  const mintNFTAndVerify = useCallback(
    async (verificationCode, ipfsUri) => {
      try {
        // --- MetaMask Flow ---
        if (metaWallet) {
          if (!walletAddress) {
            throw new Error("Wallet not connected (MetaMask).");
          }
          if (!window.ethereum || !nftContractAddress || !nftContractABI) {
            throw new Error("Ethereum environment or contract not set up (MetaMask).");
          }
          const provider = new ethers.providers.Web3Provider(window.ethereum);
          const signer = provider.getSigner();
          const nftContract = new ethers.Contract(nftContractAddress, nftContractABI, signer);
          const tx = await nftContract.mintSetNFT(walletAddress, ipfsUri, verificationCode);
          const receipt = await tx.wait();
          const tokenId = receipt.events[0]?.args?.tokenId.toString();
          if (!tokenId) {
            throw new Error("Token ID not found in transaction receipt (MetaMask).");
          }
          const response = await fetch("/update-verification-ipfs", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ verificationHash: verificationCode, ipfsUri, tokenId }),
          });
          const data = await response.json();
          if (data.error) {
            throw new Error(data.error);
          }
          alert(`NFT minted and verification updated successfully! Token ID: ${tokenId}`);
        }
        // --- Thirdweb Flow ---
        else if (thirdWallet) {
          if (!walletAddress) {
            throw new Error("Wallet not connected (Thirdweb).");
          }
          if (!nftContractInstance) {
            throw new Error("NFT contract not loaded (Thirdweb).");
          }
          // Prepare the mintSetNFT transaction call.
          const transaction = prepareContractCall({
            contract: nftContractInstance,
            method: "mintSetNFT",
            params: [walletAddress, ipfsUri, verificationCode],
          });
          // Save the verification code and ipfsUri temporarily.
          setCurrentVerificationHash(verificationCode);
          setCurrentIpfsUri(ipfsUri);
          sendTransaction(transaction, {
            onSuccess: (txResult) => {
              setNftTxHash(txResult.transactionHash);
            },
            onError: (error) => {
              console.error("Error minting NFT and verifying (Thirdweb):", error);
              alert(`Error: ${error.message}`);
            },
          });
        }
      } catch (error) {
        console.error("Error in mintNFTAndVerify:", error);
        alert(`Error: ${error.message}`);
        socket.emit("player-connected", { walletAddress, gameId });
      }
    },
    [walletAddress, nftContractInstance, sendTransaction, metaWallet, thirdWallet, gameId]
  );

  // =================== Helper: Mint Set from GameOver Screen ===================
  const handleMintSet = (setId) => {
    socket.emit("mint-set", { gameId, setId, walletAddress });
  };

  // =================== Socket Event Listeners ===================
  useEffect(() => {
    if (!isConnected) return;

    socket.on("balance-update", (updatedBalances) => {
      // Use nullish coalescing (??) to avoid defaulting to 1500 when balance is 0.
      const balanceValue = updatedBalances[walletAddress];
      const serverBalance =
        balanceValue !== undefined && balanceValue !== null
          ? parseFloat(balanceValue)
          : 1500;
      setPolyBalance(serverBalance);
    });

    socket.on("update", (data) => {
      if (data?.gameState) setGameState(data.gameState);
      if (data?.playerColors) setPlayerColors(data.playerColors);
      if (data.currentTurn) setCurrentPlayer(data.currentTurn);
      if (data.properties) setProperties(data.properties);
      if (data.playerOrder) setPlayerOrder(data.playerOrder);
      if (data.mintedSets) setMintedSets(data.mintedSets);
    });

    socket.on("dice-roll", ({ playerId, diceRoll }) => {
      setDiceRolls((prev) => ({ ...prev, [playerId]: diceRoll }));
    });

    socket.on("game-over", ({ winner, properties }) => {
      setWinner(winner);
      setProperties(properties);
      setIsGameOver(true);
    });

    socket.on("set-minted", async (payload) => {
      const { setId, success, verificationCode } = payload;
      if (!success) {
        alert("Error: The set was not successfully minted on the server side.");
        return;
      }
      alert(`Successfully minted set ${setId} in the database! Generating NFT data on server...`);
      try {
        const screenshotRes = await fetch("https://games.onlypolyverse.com/generate-screenshot", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const screenshotJson = await screenshotRes.json();
        if (screenshotJson.error) {
          throw new Error(screenshotJson.error);
        }
        const imageIpfsUri = screenshotJson.screenshotIpfsUri;
        alert(`Screenshot pinned to Pinata => ${imageIpfsUri}`);
        const metadata = {
          name: `OnlyPolyVerse Set #${setId}`,
          description: `Official property set #${setId}, verification code: ${verificationCode}`,
          image: imageIpfsUri,
          attributes: [
            { trait_type: "Set ID", value: setId },
            { trait_type: "Verification Code", value: verificationCode },
          ],
        };
        const metaRes = await fetch("https://games.onlypolyverse.com/upload-metadata", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(metadata),
        });
        const metaJson = await metaRes.json();
        if (metaJson.error) {
          throw new Error(metaJson.error);
        }
        const finalMetaUri = metaJson.metadataIpfsUri;
        alert(`Metadata pinned to IPFS => ${finalMetaUri}`);
        await mintNFTAndVerify(verificationCode, finalMetaUri);
        alert(`NFT minted successfully on-chain! => ${finalMetaUri}`);
      } catch (error) {
        console.error("Error during NFT minting process:", error);
        alert(`Error during NFT minting: ${error.message}`);
        socket.emit("player-connected", { walletAddress, gameId });
      }
    });

    socket.on("error", (msg) => {
      alert(msg);
    });

    socket.on("force-disconnect", () => {
      alert("You have been eliminated.");
      returnToWalletScreen();
    });

    socket.on("swap-proposal", (proposalData) => {
      setSwapProposal(proposalData);
      setShowSwapProposalModal(true);
    });

    socket.on("swap-declined", () => {
      alert("Your swap request was declined.");
    });

    socket.on("swap-success", () => {
      alert("Swap completed successfully!");
    });

    socket.on("turn-timer", ({ timeLeft }) => {
      setTurnTimeLeft(timeLeft);
    });

    return () => {
      socket.off("balance-update");
      socket.off("update");
      socket.off("dice-roll");
      socket.off("game-over");
      socket.off("set-minted");
      socket.off("error");
      socket.off("force-disconnect");
      socket.off("swap-proposal");
      socket.off("swap-declined");
      socket.off("swap-success");
      socket.off("turn-timer");
    };
  }, [isConnected, walletAddress, gameId, mintNFTAndVerify]);

  // =================== handleRollDice ===================
  const handleRollDice = () => {
    if (gameId && walletAddress) {
      socket.emit("roll-dice", { gameId, walletAddress });
    }
  };

  // =================== returnToWalletScreen ===================
  const returnToWalletScreen = () => {
    setIsConnected(false);
    setWalletAddress(null);
    setMetaWallet(null);
    setMetaOpolContract(null);
    setMetaOpolBalance(0);
    setThirdWallet(null);
    setThirdOpolContract(null);
    setThirdOpolBalance(0);
    setGameState({});
    setPlayerColors({});
    setCurrentPlayer(null);
    setProperties([]);
    setDiceRolls({});
    setGameId(null);
    setPlayerOrder([]);
    setMintedSets({});
    setTurnTimeLeft(60);
    window.location.reload();
  };

  // =================== Render ===================
  if (isGameOver) {
    const winnerProps = properties.filter((prop) => prop.owner_wallet === winner);
    const completeSets = Object.entries(
      winnerProps.reduce((acc, prop) => {
        if (prop.set_id) {
          acc[prop.set_id] = (acc[prop.set_id] || 0) + 1;
        }
        return acc;
      }, {})
    )
      .filter(([setId, count]) => count >= 3 && !mintedSets[setId])
      .map(([setId]) => setId);

    return (
      <div className="App">
        <h1>Game Over</h1>
        {winner ? (
          <p>Congratulations to {winner} for winning!</p>
        ) : (
          <p>No winner found (all players eliminated?).</p>
        )}
        <h2>Properties Owned:</h2>
        <ul>
          {winnerProps.length ? (
            winnerProps.map((pr) => (
              <li key={pr.property_id}>
                {pr.name} ({pr.value} POLY)
              </li>
            ))
          ) : (
            <p>No properties.</p>
          )}
        </ul>
        <h2>Mint Your Complete Sets:</h2>
        {completeSets.length > 0 ? (
          completeSets.map((setId) => (
            <div key={setId} style={{ marginBottom: "10px" }}>
              <span>Set {setId}</span>
              <button onClick={() => handleMintSet(setId)} style={{ marginLeft: "10px" }}>
                Mint Set {setId}
              </button>
            </div>
          ))
        ) : (
          <p>No complete sets available for minting.</p>
        )}
        <button onClick={returnToWalletScreen}>Return to Wallet Screen</button>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className="App" style={{ textAlign: "center" }}>
        <img src="/logo.svg" alt="Game Logo" style={{ width: "80px", marginBottom: "10px" }} />
        <h1
          style={{
            fontFamily: "'Press Start 2P', monospace",
            fontSize: "1.5rem",
            color: "#D4AF37",
            textShadow: "2px 2px 0 #6D28D9, 4px 4px 10px rgba(0,0,0,0.5)",
            marginBottom: "20px",
          }}
        >
          OnlyPolyVerse 2D
        </h1>
        <p>Please connect your crypto wallet to play.</p>
        {/* Original MetaMask connect button, styled to match */}
        <button
          onClick={connectWalletWith}
          style={{
            backgroundColor: "#007BFF",
            border: "none",
            color: "#fff",
            borderRadius: "6px",
            padding: "10px 20px",
            fontSize: "1rem",
            cursor: "pointer",
            marginRight: "10px" // Keep a small gap
          }}
        >
          Connect with MetaMask
        </button>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center", // Center the "or" plus the lines
            margin: "10px 0",
          }}
        >
          {/* Left line: fixed width of 100px (adjust as desired) */}
          <div
            style={{
              width: "85px",
              height: "1px",
              backgroundColor: "#ccc",
              marginRight: "10px",
            }}
          />

          {/* "or" text */}
          <span style={{ fontWeight: "bold", color: "#666" }}>or</span>

          {/* Right line: fixed width of 100px (adjust as desired) */}
          <div
            style={{
              width: "85px",
              height: "1px",
              backgroundColor: "#ccc",
              marginLeft: "10px",
            }}
          />
        </div>

        {/* Thirdweb ConnectButton, same shape & color, label changed */}
        <div style={{ marginTop: "10px" }}>
          <ConnectButton
            client={client}
            wallets={wallets}
            theme={darkTheme({
              colors: {
                primaryButtonBg: "hsl(211, 100%, 50%)",
                primaryButtonText: "hsl(0, 0%, 100%)",
              },
            })}
            connectButton={{ label: "Connect with Others" }}
            connectModal={{ size: "compact" }}
          />
        </div>
        <div
          style={{
            margin: "30px auto 0",
            maxWidth: "400px",
            background: "#fff",
            borderRadius: "10px",
            padding: "15px",
            boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
            textAlign: "left",
          }}
        >
          <h3
            style={{
              fontFamily: "'Press Start 2P', sans-serif",
              fontSize: "1rem",
              textAlign: "center",
              marginBottom: "10px",
            }}
          >
            Steps To Get Started
          </h3>
          <ol style={{ fontSize: "0.9rem", lineHeight: "1.4em", margin: 0 }}>
            <li style={{ marginBottom: "8px" }}>
              Connect your crypto wallet to play (this is your Player ID).
            </li>
            <li style={{ marginBottom: "8px" }}>
              Wait for other players to join before you can roll the dice.
            </li>
            <li style={{ marginBottom: "8px" }}>
              You start with 1500 Poly as your in‑game balance.
            </li>
            <li style={{ marginBottom: "8px" }}>
              Landing on corner squares lets you top up 1500 Poly with MATIC (3 POL) if short.
            </li>
            <li style={{ marginBottom: "8px" }}>
              Once you collect a set, you can Mint it as an NFT.
            </li>
            <li style={{ marginBottom: "8px" }}>
              Your NFT is your key and contract to the PolyVerse. Best of luck!
            </li>
          </ol>
        </div>
      </div>
    );
  }

  if (isJoiningGame) {
    return (
      <div className="App">
        <h1>OnlyPolyVerse 2D</h1>
        <p>Joining the game...</p>
        <div
          style={{
            width: "80%",
            height: "10px",
            margin: "0 auto",
            backgroundColor: "#ddd",
            position: "relative",
          }}
        >
          <div
            style={{
              width: "50%",
              height: "100%",
              backgroundColor: "#4caf50",
              animation: "progress 2s infinite",
            }}
          />
        </div>
        <style>
          {`
            @keyframes progress {
              0%   { width: 0%; }
              50%  { width: 100%; }
              100% { width: 0%; }
            }
          `}
        </style>
      </div>
    );
  }

  if (
    !gameState ||
    !Object.keys(gameState).length ||
    !Object.keys(playerColors).length ||
    (gameId && !properties.length)
  ) {
    return (
      <div className="App">
        <h1>OnlyPolyVerse 2D</h1>
        <p>Loading game data, please wait...</p>
      </div>
    );
  }

  return (
    <div className="App">
      <GameBoard
        gameState={gameState}
        playerColors={playerColors}
        currentPlayer={currentPlayer}
        diceResult={diceRolls[walletAddress]}
        onRollDice={handleRollDice}
        properties={properties}
        polyBalance={polyBalance}
        opolBalance={activeOpolBalance}
        diceRolls={diceRolls}
        walletAddress={walletAddress}
        gameId={gameId}
        playerOrder={playerOrder}
        opolContract={activeOpolContract}
        onTopUp={handleTopUp}
        mintedSets={mintedSets}
        turnTimeLeft={turnTimeLeft}
      />
      {showSwapProposalModal && swapProposal && (
        <div className="modal-overlay">
          <div className="modal-content modern">
            <h3>Incoming Swap Request</h3>
            <p>
              From player: <b>{swapProposal.fromPlayer}</b>
            </p>
            <p>They want to swap with you:</p>
            {(() => {
              const { offeredProperties, requestedProperties, offeredPoly, requestedPoly } = swapProposal;
              const getPropName = (id) => {
                const p = properties.find((prop) => prop.property_id === id);
                return p ? p.name : `Property #${id}`;
              };
              const requestedList = requestedProperties.length
                ? requestedProperties.map(getPropName).join(", ")
                : "none";
              const offeredList = offeredProperties.length
                ? offeredProperties.map(getPropName).join(", ")
                : "none";
              return (
                <ul>
                  <li>
                    You give: <b>{requestedList}</b> + {requestedPoly} POLY
                  </li>
                  <li>
                    They give: <b>{offeredList}</b> + {offeredPoly} POLY
                  </li>
                </ul>
              );
            })()}
            <div className="modal-buttons">
              <button
                onClick={() => {
                  socket.emit("swap-response", {
                    ...swapProposal,
                    accepted: true,
                  });
                  setShowSwapProposalModal(false);
                  setSwapProposal(null);
                }}
                className="btn btn-primary"
              >
                Accept
              </button>
              <button
                onClick={() => {
                  socket.emit("swap-response", {
                    ...swapProposal,
                    accepted: false,
                  });
                  setShowSwapProposalModal(false);
                  setSwapProposal(null);
                }}
                className="btn btn-secondary"
              >
                Decline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function AppWithProvider() {
  return (
    <ThirdwebProvider client={client} activeChain={polygon}>
      <App />
    </ThirdwebProvider>
  );
}

export default AppWithProvider;
